import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Edit2, ChevronUp, ChevronDown } from "lucide-react";

/**
 * Responsável padrão do tenant — rede de segurança do auto-assign.
 *
 * Sem ele, uma conversa que sai do robô e não casa com nenhuma regra fica
 * em "Em atendimento · Sem agente" para sempre, parecendo abandonada na
 * caixa de entrada.
 */
function DefaultAssigneeCard() {
  const utils = trpc.useUtils();
  // Ambos sob whatsapp:auto_assignment — a mesma permissão que abre esta
  // página, para o card não quebrar para quem só administra a atribuição.
  const { data: defaultAssignee, isLoading } = trpc.autoAssignment.getDefaultAssignee.useQuery();
  const { data: agents = [] } = trpc.autoAssignment.listAgents.useQuery();

  const mutation = trpc.autoAssignment.setDefaultAssignee.useMutation({
    onSuccess: () => utils.autoAssignment.getDefaultAssignee.invalidate(),
  });

  const current = defaultAssignee?.userId ?? null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Responsável padrão</CardTitle>
        <CardDescription>
          Recebe as conversas que saem do robô e não são atendidas por nenhuma regra acima.
          Sem um responsável padrão, essas conversas ficam sem agente na caixa de entrada.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Select
          value={current === null ? "none" : String(current)}
          disabled={isLoading || mutation.isPending}
          onValueChange={(value) =>
            mutation.mutate({ userId: value === "none" ? null : Number(value) })
          }
        >
          <SelectTrigger className="max-w-sm">
            <SelectValue placeholder="Selecione um atendente" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Nenhum (deixar sem agente)</SelectItem>
            {agents.map((agent) => (
              <SelectItem key={agent.id} value={String(agent.id)}>
                {agent.name || agent.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {mutation.isError && (
          <p className="text-xs text-destructive mt-2">
            Não foi possível salvar. Verifique se o atendente ainda está ativo.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function AutoAssignmentSettings() {
  const utils = trpc.useUtils();
  const { data: rules = [] } = trpc.autoAssignment.listRules.useQuery();
  const { data: tags = [] } = trpc.tags.list.useQuery();
  const { data: contacts = [] } = trpc.contacts.list.useQuery();

  const [isOpen, setIsOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    type: "round_robin" as const,
    agentIds: [] as number[],
    maxConversationsPerAgent: 10,
    priority: 0,
  });

  const createMutation = trpc.autoAssignment.createRule.useMutation({
    onSuccess: () => {
      utils.autoAssignment.listRules.invalidate();
      setIsOpen(false);
      setFormData({ name: "", description: "", type: "round_robin", agentIds: [], maxConversationsPerAgent: 10, priority: 0 });
    },
  });

  const updateMutation = trpc.autoAssignment.updateRule.useMutation({
    onSuccess: () => {
      utils.autoAssignment.listRules.invalidate();
      setEditingId(null);
      setFormData({ name: "", description: "", type: "round_robin", agentIds: [], maxConversationsPerAgent: 10, priority: 0 });
    },
  });

  const deleteMutation = trpc.autoAssignment.deleteRule.useMutation({
    onSuccess: () => utils.autoAssignment.listRules.invalidate(),
  });

  const handleSave = () => {
    if (!formData.name.trim()) return;

    if (editingId) {
      updateMutation.mutate({
        id: editingId,
        name: formData.name,
        type: formData.type,
        priority: formData.priority,
      });
    } else {
      createMutation.mutate({
        name: formData.name,
        description: formData.description,
        type: formData.type,
        agentIds: formData.agentIds,
        maxConversationsPerAgent: formData.maxConversationsPerAgent,
        priority: formData.priority,
      });
    }
  };

  return (
    <PermissionGuard permission="whatsapp:auto_assignment">
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Atribuição Automática</h1>
            <p className="text-muted-foreground mt-1">Gerencie as regras de distribuição de conversas entre agentes</p>
          </div>
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditingId(null); setFormData({ name: "", description: "", type: "round_robin", agentIds: [], maxConversationsPerAgent: 10, priority: 0 }); }}>
                <Plus className="w-4 h-4 mr-2" />
                Nova Regra
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingId ? "Editar Regra" : "Nova Regra de Atribuição"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Nome</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: VIP Round Robin"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Descrição</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descrição da regra"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Tipo de Atribuição</label>
                  <Select value={formData.type} onValueChange={(value: any) => setFormData({ ...formData, type: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="round_robin">Round Robin (Distribuição Equilibrada)</SelectItem>
                      <SelectItem value="load_based">Baseado em Carga (Menos Conversas)</SelectItem>
                      <SelectItem value="tag_based">Baseado em Tag (Especialista)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Prioridade</label>
                  <Input
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                  />
                </div>
                {(formData.type as string) === "load_based" && (
                  <div>
                    <label className="text-sm font-medium">Máx. de Conversas por Agente</label>
                    <Input
                      type="number"
                      value={formData.maxConversationsPerAgent}
                      onChange={(e) => setFormData({ ...formData, maxConversationsPerAgent: parseInt(e.target.value) || 10 })}
                    />
                  </div>
                )}
                <div className="flex gap-2">
                  <Button onClick={handleSave} disabled={createMutation.isPending || updateMutation.isPending}>
                    {editingId ? "Atualizar" : "Criar"}
                  </Button>
                  <Button variant="outline" onClick={() => setIsOpen(false)}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <DefaultAssigneeCard />

        <div className="grid gap-4">
          {rules.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center text-muted-foreground">
                Nenhuma regra de atribuição configurada. Crie uma para começar.
              </CardContent>
            </Card>
          ) : (
            rules.map((rule) => (
              <Card key={rule.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <CardTitle className="flex items-center gap-2">
                        {rule.name}
                        <Badge variant={rule.isActive ? "default" : "secondary"}>
                          {rule.isActive ? "Ativa" : "Inativa"}
                        </Badge>
                      </CardTitle>
                      <CardDescription>{rule.description}</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingId(rule.id);
                          setFormData({
                            name: rule.name,
                            description: rule.description || "",
                            type: rule.type as any,
                            agentIds: rule.agentIds || [],
                            maxConversationsPerAgent: rule.maxConversationsPerAgent || 10,
                            priority: rule.priority,
                          });
                          setIsOpen(true);
                        }}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => deleteMutation.mutate({ id: rule.id })}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Tipo:</span>
                      <p className="font-medium">
                        {rule.type === "round_robin" && "Round Robin"}
                        {rule.type === "load_based" && "Baseado em Carga"}
                        {rule.type === "tag_based" && "Baseado em Tag"}
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Prioridade:</span>
                      <p className="font-medium">{rule.priority}</p>
                    </div>
                    {rule.agentIds && rule.agentIds.length > 0 && (
                      <div className="col-span-2">
                        <span className="text-muted-foreground">Agentes:</span>
                        <div className="flex gap-1 mt-1">
                          {rule.agentIds.map((agentId) => (
                            <Badge key={agentId} variant="outline">
                              Agente {agentId}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Filtros Avançados</CardTitle>
            <CardDescription>Crie filtros personalizados para segmentar conversas</CardDescription>
          </CardHeader>
          <CardContent>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Novo Filtro
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
    </PermissionGuard>
  );
}
